<template>
  <va-modal
    id="create-update-cluster-modal"
    ref="createUpdateClusterModal"
    :cancelText="$t('clusters.buttons.back')"
    cancelClass="btn btn-micro btn-cancel"
    :okText="$t('clusters.buttons.save')"
    okClass="btn btn-micro btn-primary"
    :okToClose="false"
    @cancel="closeModal"
    @ok="submitForm"
    :value="isOpen">

    <div slot="header" class="modal-title">
      <span v-if="isUpdate">{{`${$t('clusters.labels.update_cluster') } - ${cluster.name}`}}</span>
      <span v-else>{{$t('clusters.labels.create_cluster')}}</span>
    </div>

    <form class="evollu-form">

      <div class="row">
        <div class="col-12">
          <label class="form-label">{{ $t('clusters.labels.name') }}</label>
          <va-input
            id="cluster-name-input"
            :placeholder="$t('clusters.labels.name')"
            v-model="form.data.name"
            :maxlength="255"
            @input="form.errors.clear('name')"
          />
          <small v-show="form.errors.has('name')" class="help text-danger">
            {{ $t(form.errors.get('name')) }}
          </small>
        </div>
      </div>

      <div class="row">
        <div class="col-6 d-flex align-items-center">
          <label class="form-label mr-2">{{$t('clusters.filters.distance')}}</label>
          <slider v-model="filterByDistance"></slider>
        </div>

      <FadeTransition>
          <div class="col-12 mt-3" v-if="filterByDistance">
            <div class="pb-3">
              <label for="users-limit-slider" class="form-label mb-3">{{$t('clusters.filters.radius')}}:</label>
              <va-slider
                value-visible
                v-model.number="map.filter.radius"
                :min="map.filter.min"
                :max="map.filter.max"
              />
            </div>

            <google-map
              ref="googleMaps"
              :center="map.config.center"
              :zoom="map.config.zoom"
              :fitBounds="map.config.fitBounds"
              :height="'400px'"
              :locations="map.locations"
              :shapes="map.shapes"
              @map-clicked="onClickMap"
              @circle-radius-changed="shapeRadiusChanged"
            ></google-map>

            <label class="d-flex justify-content-center" v-if="map.entities.length > 0">{{$t('clusters.filters.results', {number: map.entities.length})}}&nbsp;<a href="#" @click.prevent="applyMapFilters">{{$t('clusters.filters.apply')}}</a></label>

          </div>
      </FadeTransition>

      </div>

      <div class="row">
        <div class="col-12">
          <label class="form-label">{{$t('clusters.labels.entities')}}</label>
          <multiselect
            :value="form.data.entities"
            :options="filteredEntityOptions"
            :internal-search="false"
            @search-change="search => entitiesSearch = search.toLowerCase()"
            @select="onSelectEntity"
            @input="form.errors.clear('entities')"
            @remove="onRemoveEntity"
            track-by="id"
            label="name"
            deselect-label="×"
            select-label
            :placeholder="$t('clusters.placeholders.select_entities', {min: minNumberOfEntities})"
            :searchable="true"
            :allow-empty="true"
            :multiple="true"
            :show-labels="false"
            :close-on-select="false"
            :limit="25"
            :limitText="count => $t('clusters.placeholders.and_more', {count})"
            >
            <span slot="noResult">{{$t('patients.noResults')}}</span>
            <template slot="option" slot-scope="{ option }">
              <span v-if="option.id === -1" class="d-flex justify-content-between">
                <span v-text="option.name"></span>
                <i :class="option.icon"></i>
              </span>
              <span v-else>{{formatEntity(option)}}</span>
            </template>
            <template slot="tag" slot-scope="{ option, remove }">
              <span class="custom__tag">
                <span>{{ formatEntity(option) }}</span>
                <i @click="remove(option)" aria-hidden="true" tabindex="1" class="custom__tag-icon"></i>
              </span>
            </template>
            <template slot="clear" slot-scope="props">
              <div class="multiselect__clear" v-if="form.data.entities.length > 0" @mousedown.prevent.stop="form.data.entities = []"></div>
            </template>
          </multiselect>
          <small v-show="form.errors.has('entities')"  class="help text-danger">
            {{ $t(form.errors.get('entities')) }}
          </small>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <label class="form-label">{{$t('clusters.labels.users')}}</label>
          <multiselect
              :value="form.data.users"
              :custom-label="nameWithEntity"
              :internal-search="false"
              @search-change="search => usersSearch = search.toLowerCase()"
              :options="filteredUserOptions"
              @select="onSelectUser"
              @input="form.errors.clear('users')"
              @remove="onRemoveUser"
              track-by="id"
              label="name"
              deselect-label="×"
              :placeholder="$t('clusters.placeholders.select_users')"
              :searchable="true"
              :allow-empty="true"
              :multiple="true"
              :show-labels="false"
              :close-on-select="false"
              :limit="25"
              :limitText="count => $t('clusters.placeholders.and_more', {count})"
              >
              <span slot="noResult">{{$t('patients.noResults')}}</span>
              <template slot="option" slot-scope="{ option }">
                <span v-if="option.id === -1" class="d-flex justify-content-between">
                  <span v-text="option.name"></span>
                  <i :class="option.icon"></i>
                </span>
                <span v-else>{{option.name}} ({{option.entity}})</span>
              </template>
              <template slot="clear" slot-scope="props">
                <div class="multiselect__clear" v-if="form.data.users.length > 0" @mousedown.prevent.stop="form.data.users = []"></div>
              </template>
            </multiselect>
            <small v-show="form.errors.has('users')"  class="help text-danger">
              {{ $t(form.errors.get('users')) }}
            </small>
        </div>
      </div>
    </form>
  </va-modal>
</template>

<script>
import { debounce } from 'lodash'
import Multiselect from 'vue-multiselect'
import Errors from '@/utils/CustomNodeErrors.js'
import Slider from '@/components/common/Slider'
import GoogleMap from '@/components/maps/google-maps/GoogleMap.vue'
import FadeTransition from '@/components/common/transitions/FadeTransition'
import { ClusterService, FacilityService } from '@/api/AuthService'
const entityInfoType = 110
const readAccessType = 1

export default {
  props: {
    cluster: {
      type: Object,
      required: false
    }
  },
  components: {
    Multiselect,
    Slider,
    GoogleMap,
    FadeTransition
  },
  created: function () {
    Promise.all([this.createUsersDataset(), this.createEntityDataset()])
      .then(() => {
        if (this.isUpdate) {
          this.prepareForUpdate()
        }
        this.addAuthenticatedUserToList()
      })
  },
  mounted: function () {
    this.isOpen = true
  },
  computed: {
    isUpdate: function () {
      return this.cluster !== null && this.cluster !== undefined
    },
    authEntities: function () {
      return this.$store.state.services.find(s => s.url === this.$store.state.evollauth_server).entities
    },
    filteredEntityOptions: function () {
      let filteredOptions

      if (this.entitiesSearch) {
        filteredOptions = this.entityOptions.filter(element => element.name.toLowerCase().includes(this.entitiesSearch) || (element.hasOwnProperty('cities') && element.cities.length > 0 ? element.cities.some(city => city.toLowerCase().includes(this.entitiesSearch)) : false))
      } else {
        filteredOptions = [...this.entityOptions]
      }
      if (filteredOptions.length > 0) {
        let isSelectAll = !filteredOptions.every(option => this.form.data.entities.some(entity => entity.id === option.id))
        filteredOptions.unshift(this.prepareFirstOption(isSelectAll, this.entitiesSearch.length > 0))
      }
      return filteredOptions
    },
    filteredUserOptions: function () {
      let filteredOptions

      if (this.usersSearch) {
        filteredOptions = this.userOptions.filter(element => element.name.toLowerCase().includes(this.usersSearch) || element.entity.toLowerCase().includes(this.usersSearch))
      } else {
        filteredOptions = [...this.userOptions]
      }
      if (filteredOptions.length > 0) {
        let isSelectAll = !filteredOptions.every(option => this.form.data.users.some(user => user.id === option.id))
        filteredOptions.unshift(this.prepareFirstOption(isSelectAll, this.usersSearch.length > 0))
      }
      return filteredOptions
    },
    configurationsService: function () {
      return this.$store.state.services.find(service => service.service === 'configurations')
    }
  },
  data: function () {
    return {
      isOpen: false,
      userOptions: [],
      entityOptions: [],
      minNumberOfEntities: 2,
      entitiesSearch: '',
      usersSearch: '',
      filterByDistance: false,
      map: {
        config: {
          center: { lat: 39.7432886, lng: -8.8197383 },
          zoom: 7,
          fitBounds: false
        },
        locations: [],
        shapes: [],
        filter: {
          radius: 25,
          min: 1,
          max: 250
        },
        entities: []
      },
      multiselectLabelOptions: {
        true: { // Select All
          true: 'clusters.labels.select_all_filtered',
          false: 'clusters.labels.select_all',
          icon: 'fa fa-list-ul'
        },
        false: { // Deselect All
          true: 'clusters.labels.deselect_all_filtered',
          false: 'clusters.labels.deselect_all',
          icon: 'fa fa-times'
        }
      },
      form: {
        data: {
          name: '',
          entities: [],
          users: []
        },
        errors: new Errors()
      }
    }
  },
  methods: {
    getFormData: function () {
      return {
        name: this.form.data.name,
        entities: this.form.data.entities.map(entity => entity.id),
        users: this.form.data.users.map(user => user.id)
      }
    },
    nameWithEntity ({ name, entity }) {
      if (!entity) return name
      return name + ' (' + entity + ')'
    },
    createMarkerInfoWidget: function (facility) {
      let contentString = '<div>' +
      '<h5>' + facility.name + '</h5>' +
      '<b>' + `${this.$t('facility.inputs.address')}` + ': </b>' +
      '<span v-if="facility.address.line1">' + facility.address.line1 + ', </span>' +
      '<span v-if="facility.address.line2">' + facility.address.line2 + ', </span>' +
      '<span v-if="facility.address.city">' + facility.address.city + ', </span>' +
      '<span v-if="facility.address.postal_code">' + facility.address.postal_code + '</span>' +
      '<br>' +
      '<b>' + `${this.$t('placings.coordinates')}` + ':</b> ' +
      '<span v-if="facility.address.lat">' + facility.address.lat + ', </span>' +
      '<span v-if="facility.address.lng">' + facility.address.lng + '</span>' +
      '</div>'
      return {
        content: contentString,
        open: false
      }
    },
    resetMap: function () {
      this.map.entities = []
      this.map.locations = []
      this.map.shapes = []
    },
    applyMapFilters: function () {
      let entitiesNotInCluster = this.map.entities.filter(entity => !this.form.data.entities.some(e => e.id === entity.id))
      this.form.data.entities = this.form.data.entities.concat(entitiesNotInCluster)
      this.resetMap()
    },
    shapeRadiusChanged: function ({ radius, circle }) {
      if (radius > this.map.filter.max) {
        this.map.filter.radius = this.map.filter.max
        circle.radius = this.map.filter.radius * 1000
      } else {
        this.map.filter.radius = radius
      }
      if (this.map.filter.radius > 0) {
        this.loadFacilitiesByDistance(this.map.config.center)
      }
    },
    onClickMap: function ({ lat, lng }) {
      this.$refs.googleMaps.clearCircles()
      this.map.shapes = []
      let position = { lat, lng }
      this.map.shapes.push({
        center: position,
        radius: parseInt(this.map.filter.radius) * 1000,
        draggable: true
      })
      this.$refs.googleMaps.updateCenter({ lat, lng })
      this.map.config.center = { lat, lng }
      if (this.map.filter.radius > 0) {
        this.loadFacilitiesByDistance(position)
      }
    },
    loadFacilitiesByDistance: function ({ lat, lng }) {
      FacilityService.getAll({
        lat,
        lng,
        radius: this.map.filter.radius
      })
        .then(facilities => {
          this.map.filteredFacilities = facilities
          this.map.locations = []
          if (this.map.filteredFacilities.length > 0) {
            let filteredFacilities = this.map.filteredFacilities.filter(facility => facility.address && facility.address.latitude && facility.address.longitude && this.authEntities.some(e => e.id === facility.entity_id))

            this.map.locations = filteredFacilities.map(facility => {
              return {
                label: facility.name,
                position: {
                  lat: facility.address.latitude,
                  lng: facility.address.longitude
                },
                window: this.createMarkerInfoWidget(facility)
              }
            })
            this.map.entities = this.authEntities.filter(e => filteredFacilities.some(facility => e.id === facility.entity_id))
          }
        }).catch((err) => {
          console.log(err)
        })
    },
    closeModal: function () {
      this.isOpen = false
      setTimeout(() => this.$emit('cancel'), 500)
    },
    prepareForUpdate: function () {
      this.form.data.name = this.cluster.name
      this.form.data.entities = this.cluster.entities.filter(clusterEntity => this.entityOptions.find(entityOption => clusterEntity.id === entityOption.id))
      this.form.data.users = this.cluster.users.filter(clusterUser => this.userOptions.find(userOption => clusterUser.id === userOption.id))
    },
    createUsersDataset: function () {
      return new Promise((resolve, reject) => {
        this.$http.get(`${this.configurationsService.url}/users`)
          .then(response => {
            this.userOptions = response.data.data
            this.userOptions = this.userOptions.sort((a, b) => a.name.localeCompare(b.name))
            resolve()
          })
          .catch(errors => reject(errors))
      })
    },
    createEntityDataset: function () {
      return new Promise((resolve, reject) => {
        let entityPermissions = this.configurationsService.user_permissions.filter(permission => permission.info_type === entityInfoType && permission.access_type >= readAccessType)

        if (entityPermissions.some(permission => permission.entity_id === null)) {
          this.entityOptions = [...this.configurationsService.entities]
        } else {
          this.entityOptions = this.configurationsService.entities.filter(entity => entityPermissions.some(permission => permission.entity_id === entity.id))
        }

        this.entityOptions.sort((a, b) => a.name.localeCompare(b.name))
        resolve()
      })
    },
    addAuthenticatedUserToList: function () {
      if (!this.isUpdate) {
        this.form.data.users.push(this.userOptions.find(user => user.id === this.$auth.user().id))
      }
    },
    submitForm: function () {
      this.form.errors.clear()
      const data = this.getFormData()
      let request = this.isUpdate ? ClusterService.update(this.cluster.id, data) : ClusterService.store(data)

      request
        .then(cluster => {
          this.$emit(this.isUpdate ? 'clusterUpdated' : 'clusterCreated', { cluster })
          this.showToast(this.$t(this.isUpdate ? 'clusters.messages.successful_update' : 'clusters.messages.successful_creation'), this.$store.state.successToastOptions)
          this.$refs.createUpdateClusterModal.cancel()
        })
        .catch(errors => {
          if (errors.response && errors.response.status === 422) {
            this.form.errors.record(errors.response.data.errors, 'clusters.errors.')
            this.showToast(this.$t('clusters.messages.form_contains_errors'), this.$store.state.errorToastOptions)
          } else {
            this.showToast(this.$t('clusters.messages.something_went_wrong'), this.$store.state.errorToastOptions)
          }
        })
    },
    prepareFirstOption: function (isSelectAll, searchIsActive, id = -1) {
      return {
        id,
        name: this.$t(this.multiselectLabelOptions[isSelectAll][searchIsActive]),
        icon: this.multiselectLabelOptions[isSelectAll].icon,
        isSelectAll
      }
    },
    onSelectEntity: function (selectedEntity) {
      if (selectedEntity.id === -1) {
        this.onSelectAllEntities(selectedEntity.isSelectAll)
      } else {
        this.form.data.entities.push(selectedEntity)
      }
    },
    onSelectAllEntities: function (isSelectAll) {
      if (isSelectAll) {
        if (this.entitiesSearch) { // Search active, select only ones on the filtered list
          this.filteredEntityOptions.slice(1).forEach(option => {
            if (!this.form.data.entities.some(entity => entity.id === option.id)) { // avoid duplicates
              this.form.data.entities.push(option)
            }
          })
        } else { // No search, select all options
          this.form.data.entities = this.filteredEntityOptions.slice(1)
        }
      } else {
        if (this.entitiesSearch) { // Search active, remove only ones on the filtered list
          this.form.data.entities = this.form.data.entities.filter(entity => !this.filteredEntityOptions.some(option => entity.id === option.id))
        } else { // No search, remove all options
          this.form.data.entities = []
        }
      }
    },
    onRemoveEntity: function (removedEntity) {
      if (removedEntity.id !== -1) {
        this.form.data.entities = this.form.data.entities.filter(element => element.id !== removedEntity.id)
      }
    },
    onSelectUser: function (selectedUser) {
      if (selectedUser.id === -1) {
        this.onSelectAllUsers(selectedUser.isSelectAll)
      } else {
        this.form.data.users.push(selectedUser)
      }
    },
    onSelectAllUsers: function (isSelectAll) {
      if (isSelectAll) {
        if (this.usersSearch) {
          this.filteredUserOptions.slice(1).forEach(option => {
            if (!this.form.data.users.some(user => user.id === option.id)) {
              this.form.data.users.push(option)
            }
          })
        } else {
          this.form.data.users = this.filteredUserOptions.slice(1)
        }
      } else {
        if (this.usersSearch) {
          this.form.data.users = this.form.data.users.filter(user => !this.filteredUserOptions.some(option => user.id === option.id))
        } else {
          this.form.data.users = []
        }
      }
    },
    onRemoveUser: function (removedUser) {
      if (removedUser.id !== -1) {
        this.form.data.users = this.form.data.users.filter(element => element.id !== removedUser.id)
      }
    },
    formatEntity: function (entity) {
      return `${entity.name} ${entity.cities && entity.cities.length ? '(' + entity.cities.join(', ') + ')' : ''}`
    }
  },
  watch: {
    'map.filter.radius': debounce(function (value) {
      if (value > 0) {
        if (this.map.locations.length > 0) {
          this.map.shapes = []
          this.map.shapes.push({
            center: this.map.config.center,
            radius: parseInt(value) * 1000,
            draggable: true
          })
          this.loadFacilitiesByDistance(this.map.config.center)
        }
      }
    }, 750)
  }
}
</script>
